import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dataService } from '@/services/dataService';
import { Partnership } from '@/types/entities';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { exportRowsToCsv } from '@/lib/exportCsv';
import { useI18n } from '@/i18n';
import { useToast } from '@/components/ui/Toast';
import { PartnershipFormDrawer, PartnershipFormValues } from './PartnershipFormDrawer';

export function PartnershipsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const push = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<Partnership[]>([]);
  const [formOpen, setFormOpen] = useState(searchParams.get('new') === '1');
  const [archiveTarget, setArchiveTarget] = useState<Partnership | null>(null);

  function reload() {
    dataService.listPartnerships().then(setRows);
  }
  useEffect(reload, []);

  async function handleCreate(values: PartnershipFormValues) {
    const created = await dataService.createPartnership(values);
    push(`${t('toast.partnershipCreated')} — ${created.id}`, 'success');
    setFormOpen(false);
    setSearchParams({});
    reload();
  }
  async function confirmArchive() {
    if (!archiveTarget) return;
    await dataService.archivePartnership(archiveTarget.id);
    push(t('toast.partnershipArchived'), 'warn');
    setArchiveTarget(null);
    reload();
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{t('nav.partnerships')}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => {
            exportRowsToCsv('partnerships', rows, [
              { key: 'id', header: 'ID', value: (p) => p.id },
              { key: 'name', header: 'Partner', value: (p) => p.partnerName },
              { key: 'status', header: 'Status', value: (p) => p.status },
            ]);
            dataService.logExport('Partnerships');
          }}>{t('common.export')}</button>
          <button className="btn-primary" onClick={() => setFormOpen(true)}>+ {t('partnershipForm.newPartnership')}</button>
        </div>
      </div>

      <div className="card">
        {rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ color: 'var(--text-3)', marginBottom: 12 }}>{t('empty.noPartnerships')}</div>
            <button className="btn-primary" onClick={() => setFormOpen(true)}>+ {t('partnershipForm.newPartnership')}</button>
          </div>
        ) : (
          <div className="table-scroll">
          <table className="dt">
            <thead><tr><th>ID</th><th>Partner</th><th>Type</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} onClick={() => navigate(`/partnerships/${p.id}`)}>
                  <td>{p.id}</td>
                  <td>{p.partnerName}</td>
                  <td>{p.type}</td>
                  <td><StatusBadge entity="partnership" statusKey={p.status} /></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button style={{ color: 'var(--warn)' }} onClick={() => setArchiveTarget(p)}>{t('rowMenu.archive')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {formOpen && <PartnershipFormDrawer onClose={() => { setFormOpen(false); setSearchParams({}); }} onSubmit={handleCreate} />}

      <ConfirmModal
        open={!!archiveTarget}
        title={t('confirm.archivePartnershipTitle')}
        recordName={archiveTarget ? `${archiveTarget.id} — ${archiveTarget.partnerName}` : undefined}
        description={t('confirm.archivePartnershipDesc')}
        reversible
        confirmLabel={t('rowMenu.archive')}
        onConfirm={confirmArchive}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
}
